import { useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { money } from "@/lib/format";
import { Search, Plus, AlertTriangle } from "lucide-react";
import { PhoneIdentitySection } from "./PhoneIdentitySection";
import { PinReasonDialog } from "./PinReasonDialog";

const normPhone = (p) => (p || "").replace(/\D/g, "");

// Shared "search existing customer or add a new one" flow — single source of truth for
// POS and Quotations (and any future caller). Duplicate detection (phone/email) now runs
// live as the operator types via `PhoneIdentitySection` (POS-0034 Stage 3), with the same
// Manager/Owner-PIN shared-phone override used on the Customers page. The old unaudited
// "Create anyway" bypass is gone — any genuine duplicate must go through that approval.
//
// `customers`: the caller's own customer list (caller owns this state; the modal never
// re-fetches it). `onSelect(customer)`: called for an existing pick AND for a freshly
// created customer (the full record from the POST response) — the caller decides how to
// merge it into its own list (append if new) and what "selected" means for its own flow.
// `renderBadges(c)`: optional extra line under each result row (POS shows credit/balance
// info here; callers that don't need it can omit the prop entirely).
export default function CustomerPickerModal({ open, onOpenChange, customers, onSelect, renderBadges, title = "Select Customer", source = "quick_add", loading = false, transactionLabel = "sale", onCustomerUpdated }) {
  const [custSearch, setCustSearch] = useState("");
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAdd, setQuickAdd] = useState({ name: "", phone: "", email: "", address: "", notes: "" });
  const [dupMatches, setDupMatches] = useState([]);
  const [qaSaving, setQaSaving] = useState(false);
  const [legacyCustomer, setLegacyCustomer] = useState(null);

  const resetQuickAdd = () => { setQuickAdd({ name: "", phone: "", email: "", address: "", notes: "" }); setDupMatches([]); };
  const closeAll = () => { onOpenChange(false); setQuickAddOpen(false); resetQuickAdd(); setCustSearch(""); };

  const custFiltered = useMemo(() => {
    const s = custSearch.trim().toLowerCase();
    if (!s) return customers;
    return customers.filter((c) =>
      c.name.toLowerCase().includes(s) ||
      (c.company || "").toLowerCase().includes(s) ||
      (normPhone(s) && normPhone(c.phone).includes(normPhone(s))));
  }, [customers, custSearch]);

  const pick = (c) => {
    onSelect(c); closeAll();
    // POS-0034 Stage 3/5: never interrupt the active sale — this is a non-blocking follow-up only.
    // Missing phone is detected instantly client-side; an invalid/ambiguous LEGACY phone (e.g.
    // letters, an extension, a bare 7-digit number) needs the same backend validate call every
    // other surface uses, fired here without awaiting it so selection/checkout is never delayed.
    if (!c.phone || !c.phone.trim()) {
      toast.warning(`${c.name}'s phone number is missing`, {
        description: `This ${transactionLabel} isn't affected — you can update it anytime.`,
        action: { label: "Update now", onClick: () => setLegacyCustomer(c) },
        cancel: { label: "Dismiss", onClick: () => {} },
      });
    } else {
      api.get("/customers/phone/validate", { params: { phone: c.phone, region: c.phone_region || "JM" } })
        .then(({ data }) => {
          if (data.status !== "valid") {
            toast.warning(`${c.name}'s phone number looks incomplete or invalid`, {
              description: `This ${transactionLabel} isn't affected — you can update it anytime.`,
              action: { label: "Update now", onClick: () => setLegacyCustomer(c) },
              cancel: { label: "Dismiss", onClick: () => {} },
            });
          }
        })
        .catch(() => {}); // non-blocking: a validate hiccup never surfaces as an error here
    }
  };

  const selectExisting = (c) => { toast.success(`${c.name} selected`); pick(c); };

  const saveQuickAdd = async () => {
    if (!quickAdd.name.trim() || !quickAdd.phone.trim()) { toast.error("Name and phone are required"); return; }
    if (qaSaving) return;
    setQaSaving(true);
    try {
      // POS-0034 Stage 5 fix: mirror the Customers page's own validate-before-save check so quick-add
      // (POS/Quotations) can't save a phone its own inline warning already flagged as not valid.
      const v = await api.get("/customers/phone/validate", { params: { phone: quickAdd.phone, region: quickAdd.phone_region || "JM" } });
      if (v.data.status !== "valid") {
        toast.error(v.data.status === "ambiguous_short"
          ? "Add the area code — a 7-digit number alone isn't accepted for new customers"
          : "Please enter a valid phone number");
        return;
      }
      const { data } = await api.post("/customers", {
        name: quickAdd.name.trim(), phone: quickAdd.phone.trim(),
        email: quickAdd.email.trim(), address: quickAdd.address.trim(),
        notes: quickAdd.notes.trim(), source, approval_id: quickAdd.approval_id, phone_region: quickAdd.phone_region,
      });
      toast.success(`${data.name} added and selected`);
      pick(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.duplicate && d?.matches?.length) { setDupMatches(d.matches); return; }
      toast.error(apiError(d));
    } finally { setQaSaving(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setCustSearch(""); }}>
        <DialogContent className="max-w-md" data-testid="customer-picker">
          <DialogHeader><DialogTitle className="font-head">{title}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Search for a customer or add a new one.</DialogDescription>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={custSearch} onChange={(e) => setCustSearch(e.target.value)} placeholder="Search by name, company or phone…"
              className="pl-9" data-testid="customer-search" />
          </div>
          <Button variant="outline" className="w-full justify-start border-dashed" data-testid="quick-add-customer-btn"
            onClick={() => { setQuickAdd((q) => ({ ...q, name: custSearch.trim() && isNaN(Number(custSearch)) ? custSearch.trim() : "" })); setDupMatches([]); setQuickAddOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" /> Add new customer
          </Button>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {loading && (
              <p className="text-center text-sm text-muted-foreground py-6" data-testid="customer-loading">Loading customers…</p>
            )}
            {!loading && custFiltered.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-6" data-testid="customer-no-match">
                No customer found. Use “Add new customer” above.
              </p>
            )}
            {!loading && custFiltered.map((c) => (
              <button key={c.id} onClick={() => pick(c)} data-testid={`customer-opt-${c.id}`}
                className="w-full text-left p-3 rounded-xl border border-border hover:border-emerald-500/50 hover:bg-secondary">
                <div className="flex justify-between gap-2">
                  <p className="font-semibold text-sm">{c.name}</p>
                  <span className="text-[11px] text-muted-foreground">{c.account_status && c.account_status !== "active" ? c.account_status.toUpperCase() : ""}</span>
                </div>
                <p className="text-xs text-muted-foreground">{[c.company, c.phone].filter(Boolean).join(" · ") || "—"}</p>
                {renderBadges ? renderBadges(c) : null}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={quickAddOpen} onOpenChange={(o) => { setQuickAddOpen(o); if (!o) resetQuickAdd(); }}>
        <DialogContent className="max-w-md" data-testid="quick-add-dialog">
          <DialogHeader><DialogTitle className="font-head">Add Customer</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Quickly add a customer. Name and phone are required.</DialogDescription>
          {dupMatches.length > 0 && (
            <div className="space-y-3" data-testid="quick-add-dup-warning">
              <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 p-3 text-sm flex gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>A customer with a matching phone or email already exists. Use the existing record, or go back to request a shared-phone approval.</span>
              </div>
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {dupMatches.map((c) => (
                  <button key={c.id} onClick={() => selectExisting(c)} data-testid={`dup-use-${c.id}`}
                    className="w-full text-left p-3 rounded-xl border border-border hover:border-emerald-500/50 hover:bg-secondary">
                    <p className="font-semibold text-sm">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.phone}{c.email ? ` · ${c.email}` : ""} · Balance {money(c.outstanding_balance)}</p>
                  </button>
                ))}
              </div>
              <Button variant="outline" className="w-full" onClick={() => setDupMatches([])} data-testid="dup-back-btn">Back</Button>
            </div>
          )}
          <div className="space-y-3" style={dupMatches.length > 0 ? { display: "none" } : undefined}>
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={quickAdd.name} onChange={(e) => setQuickAdd({ ...quickAdd, name: e.target.value })} data-testid="qa-name" placeholder="Customer name" />
            </div>
            <PhoneIdentitySection form={quickAdd} setForm={setQuickAdd} name={quickAdd.name} customerId={null}
              required source={source} testIdPrefix="qa-phone" seedMatches={dupMatches} />
            <div>
              <Label className="text-xs">Email</Label>
              <Input value={quickAdd.email} onChange={(e) => setQuickAdd({ ...quickAdd, email: e.target.value })} data-testid="qa-email" placeholder="Optional" />
            </div>
            <div>
              <Label className="text-xs">Address</Label>
              <Input value={quickAdd.address} onChange={(e) => setQuickAdd({ ...quickAdd, address: e.target.value })} data-testid="qa-address" placeholder="Optional" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input value={quickAdd.notes} onChange={(e) => setQuickAdd({ ...quickAdd, notes: e.target.value })} data-testid="qa-notes" placeholder="Optional" />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Credit mode will follow your business default. Full details can be completed later from Customers.
            </p>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => { setQuickAddOpen(false); resetQuickAdd(); }} data-testid="qa-cancel">Cancel</Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={qaSaving} onClick={() => saveQuickAdd()} data-testid="qa-save">{qaSaving ? "Adding…" : "Add & Select"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!legacyCustomer} onOpenChange={(o) => !o && setLegacyCustomer(null)}>
        <DialogContent className="max-w-sm" data-testid="legacy-phone-dialog">
          <DialogHeader><DialogTitle className="font-head">Update Phone · {legacyCustomer?.name}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Update this customer's phone number, or skip for now with a Manager/Owner PIN.</DialogDescription>
          {legacyCustomer && <LegacyPhoneEditor customer={legacyCustomer} onDone={() => setLegacyCustomer(null)} onCustomerUpdated={onCustomerUpdated} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

// POS-0034 Stage 3: the non-blocking legacy-phone follow-up shown after selecting an existing
// customer in POS/Quotations whose phone is missing. Update (with the same shared-phone
// override path) or record an audited Manager/Owner-PIN skip — never blocks the active sale,
// since it only ever opens from the toast action, after `pick()` has already completed.
function LegacyPhoneEditor({ customer, onDone, onCustomerUpdated }) {
  const [form, setForm] = useState({ phone: customer.phone || "" });
  const [saving, setSaving] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);

  const save = async () => {
    if (!form.phone?.trim()) { toast.error("Enter a phone number, or use Skip for now."); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/customers/${customer.id}`, { phone: form.phone.trim(), approval_id: form.approval_id, phone_region: form.phone_region });
      toast.success("Phone updated");
      onCustomerUpdated?.(data);
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <PhoneIdentitySection form={form} setForm={setForm} name={customer.name} customerId={customer.id}
        required={false} source="legacy_prompt" testIdPrefix="legacy-phone" />
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={() => setSkipOpen(true)} data-testid="legacy-skip-btn">Skip (Manager/Owner PIN)</Button>
        <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={saving} onClick={save} data-testid="legacy-save-btn">{saving ? "Saving…" : "Save"}</Button>
      </div>
      <PinReasonDialog open={skipOpen} onOpenChange={setSkipOpen} title="Skip legacy phone update"
        description={`Record that updating ${customer.name}'s phone was skipped for now.`} submitLabel="Confirm skip"
        onConfirm={async (reason, pin) => {
          await api.post(`/customers/${customer.id}/phone-skip`, { reason, manager_pin: pin });
          toast.success("Skip recorded");
          onDone();
        }} />
    </div>
  );
}
